import { useState, useEffect, useCallback } from "react";
import usePostHook from "../customHooks/usePostHook";
import APIS from "../constant/Endpoint";

// Convert VAPID key to Uint8Array
const urlB64ToUint8Array = (base64String: string) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

// Check if push notifications are enabled
const isPushNotificationEnable = async (): Promise<boolean> => {
  const status = Notification.permission;
  if (status !== "granted") return false;

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  return !!subscription;
};

// Custom Hook
export const usePushNotifications = () => {
  const { mutateAsync, isPostLoading } = usePostHook({
    queryKey: ["push-notification"],
  });
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const VAPID_PUBLIC_KEY = "BFmvbxrLDtHUIXH3rmoQ7BttNvgCD-TN3K_UM3bcE5pQ318MhWwx5ZWmy9gv_tzxKCzyrqT4OH1rhmqt4C29dvc";

  useEffect(() => {
    const checkStatus = async () => {
      const status = await isPushNotificationEnable();
      setIsEnabled(status);
    };
    checkStatus();
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!("serviceWorker" in navigator)) {
      console.warn("Service workers are not supported in this browser.");
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.warn("User denied notifications.");
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const applicationServerKey = urlB64ToUint8Array(VAPID_PUBLIC_KEY);

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      console.log("Subscribed:", subscription);
      setIsEnabled(true);
      try {
        const res = await mutateAsync({
          url: `${APIS.PUSH_NOTIFICATION}register/`,
          formData: { ...subscription.toJSON() },
          setError: () => { },
        });
        if (res?.status === 200 || 201) {
          console.log("Subscription saved successfully.");
        } else {
        }
      } catch (err) {
        console.error("Failed to save subscription:", err);
      }
      return subscription.toJSON();

    } catch (error) {
      console.error("Subscription failed:", error);
      return null;
    }
  }, []);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
      console.log("Unsubscribed from push notifications.");
      setIsEnabled(false);
    }
  }, []);

  // Toggle push notifications
  const togglePushNotifications = useCallback(() => {
    if (isEnabled) {
      unsubscribe();
    } else {
      subscribe();
    }
  }, [isEnabled, subscribe, unsubscribe]);

  return { isEnabled, subscribe, unsubscribe, togglePushNotifications };
};
